import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PayoutConfirmationStage } from '@hub/components/mail-confirmation-nova/payout-confirmation-stage';
import { WithdrawalConfirmedStage } from '@hub/components/mail-confirmation-nova/withdrawal-confirmed-stage';
import { FormControlLabel } from '@mui/material';
import { getPayoutOptions } from '@monorepo/client-types';
import { DesignIcon } from '@monorepo/nova-icons';
import { Checkbox, Stack, Typography, useTheme } from '@monorepo/nova-ui';
import {
  useCryptoWithdrawalLimit,
  usePayoutForm,
  usePayoutSubmission,
} from './hooks';
import { PayoutRequestFormProps, PayoutStage } from './types';
import { getPaymentForm } from './utils';
import {
  FormActions,
  PaymentMethodsSection,
  WithdrawalAmountSection,
} from './withdraw-components';

export const PayoutRequestForm: React.FC<PayoutRequestFormProps> = (props) => {
  const {
    maxWithdrawal,
    showAmountSelection,
    source,
    sourceId,
    modalRef,
    validateWithDrawEligible,
    credits,
    userPayoutPercentage,
    isAffiliate = false,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation(['withdrawFundsNova']);

  // Form state and validation
  const {
    form,
    selectedPaymentOption,
    whatUserGets,
    handleMinimumValidation,
    isSubmitDisabled,
  } = usePayoutForm({
    maxWithdrawal,
    credits,
    userPayoutPercentage,
    source,
  });

  // Crypto-specific logic and limits
  const { maxWithdrawalLimit, maximumWithdrawalMessage } =
    useCryptoWithdrawalLimit({
      maxWithdrawal,
      selectedPaymentOption,
    });

  // Form submission and API handling
  const {
    beneficiaryFormRef,
    isLoading,
    handleSubmit,
    payoutStage,
    payoutData,
    handleConfirmPayout,
    handleCancelConfirmation,
    handleSuccessClose,
  } = usePayoutSubmission({
    source,
    sourceId,
    maxWithdrawal,
    showAmountSelection,
    validateWithDrawEligible,
    modalRef,
    form,
    isAffiliate,
  });

  // Render confirmation stage (affiliate staged flow only)
  if (isAffiliate && payoutStage === PayoutStage.Confirmation && payoutData) {
    return (
      <PayoutConfirmationStage
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmPayout}
        isLoading={isLoading}
      />
    );
  }

  // Render success stage (affiliate staged flow only)
  if (isAffiliate && payoutStage === PayoutStage.Success) {
    return <WithdrawalConfirmedStage onClose={handleSuccessClose} />;
  }

  // Render form stage (default)
  return (
    <Stack
      direction={'column'}
      gap={6}
      sx={{
        overflowY: 'auto',
        maxHeight: `calc(100vh - ${theme.spacing(50)})`,
      }}
    >
      {!isAffiliate && (
        <DesignIcon
          iconName={'DollarNote'}
          sx={{
            fontSize: theme.typography.pxToRem(120),
          }}
          useMuiSvgIcon={false}
        />
      )}
      <Typography variant={'h3'}>
        {isAffiliate
          ? t('withdrawFundsNova:affiliate_payout_request_title')
          : t('withdrawFundsNova:payout_request_title')}
      </Typography>
      <WithdrawalAmountSection
        form={form}
        maxWithdrawalLimit={maxWithdrawalLimit}
        whatUserGets={whatUserGets}
        maximumWithdrawalMessage={maximumWithdrawalMessage}
        handleMinimumValidation={handleMinimumValidation}
        userPayoutPercentage={userPayoutPercentage}
        isAffiliate={isAffiliate}
      />
      <Stack direction={'column'} gap={2}>
        <PaymentMethodsSection form={form} payoutOptions={getPayoutOptions()} />
        {getPaymentForm(selectedPaymentOption, form, beneficiaryFormRef)}
        <Typography>{t('withdrawFundsNova:withdrawals_info')}</Typography>
        <Controller
          rules={{ validate: (value) => value }}
          name={'agreedToTerms'}
          control={form.control}
          render={({ field }) => (
            <FormControlLabel
              label={t('withdrawFundsNova:agree_to_terms_label')}
              control={<Checkbox {...field} sx={{ padding: 0 }} />}
            />
          )}
        />
      </Stack>
      <FormActions
        isLoading={isLoading}
        isDisabled={isSubmitDisabled}
        onSubmit={handleSubmit}
      />
    </Stack>
  );
};
